import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PatientInformationForm = ({ patientData, onPatientDataChange, errors = {} }) => {
  const [showSensitiveInfo, setShowSensitiveInfo] = useState(false);

  const ageGroupOptions = [
    { value: 'neonate', label: 'Neonate (0-27 days)' },
    { value: 'infant', label: 'Infant (28 days - 23 months)' },
    { value: 'child', label: 'Child (2-11 years)' },
    { value: 'adolescent', label: 'Adolescent (12-17 years)' },
    { value: 'adult', label: 'Adult (18-64 years)' },
    { value: 'elderly', label: 'Elderly (65+ years)' }
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'unknown', label: 'Unknown' }
  ];

  const handleInputChange = (field, value) => {
    onPatientDataChange({
      ...patientData,
      [field]: value
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="User" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Patient Information</h3>
          <p className="text-sm text-muted-foreground">Anonymized patient demographics and medical history</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Patient ID */}
        <Input
          label="Patient ID"
          type="text"
          placeholder="Auto-generated or manual entry"
          value={patientData?.patientId || ''}
          onChange={(e) => handleInputChange('patientId', e?.target?.value)}
          error={errors?.patientId}
          description="Anonymized identifier for patient tracking"
          className="col-span-full"
        />

        {/* Age Group */}
        <Select
          label="Age Group"
          options={ageGroupOptions}
          value={patientData?.ageGroup || ''}
          onChange={(value) => handleInputChange('ageGroup', value)}
          error={errors?.ageGroup}
          required
          placeholder="Select age category"
        />

        {/* Gender */}
        <Select
          label="Gender"
          options={genderOptions}
          value={patientData?.gender || ''}
          onChange={(value) => handleInputChange('gender', value)}
          error={errors?.gender}
          placeholder="Select gender"
        />

        {/* Weight */}
        <Input
          label="Weight (kg)"
          type="number"
          placeholder="Patient weight"
          value={patientData?.weight || ''}
          onChange={(e) => handleInputChange('weight', e?.target?.value)}
          error={errors?.weight}
          min="0"
          max="300"
          step="0.1"
        />

        {/* Height */}
        <Input
          label="Height (cm)"
          type="number"
          placeholder="Patient height"
          value={patientData?.height || ''}
          onChange={(e) => handleInputChange('height', e?.target?.value)}
          error={errors?.height}
          min="0"
          max="250"
        />

        {/* Medical History */}
        <div className="col-span-full">
          <label className="block text-sm font-medium text-foreground mb-2">
            Relevant Medical History
          </label>
          <textarea
            className="w-full min-h-[100px] px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
            placeholder="Enter relevant medical conditions, allergies, and concurrent medications..."
            value={patientData?.medicalHistory || ''}
            onChange={(e) => handleInputChange('medicalHistory', e?.target?.value)}
          />
          {errors?.medicalHistory && (
            <p className="text-sm text-error mt-1">{errors?.medicalHistory}</p>
          )}
        </div>

        {/* Concomitant Medications */}
        <div className="col-span-full">
          <label className="block text-sm font-medium text-foreground mb-2">
            Concomitant Medications
          </label>
          <textarea
            className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
            placeholder="List all medications taken concurrently with the suspect drug..."
            value={patientData?.concomitantMeds || ''}
            onChange={(e) => handleInputChange('concomitantMeds', e?.target?.value)}
          />
        </div>

        {/* Pregnancy Status */}
        {patientData?.gender === 'female' && (
          <div className="col-span-full">
            <Checkbox
              label="Patient is pregnant or breastfeeding"
              checked={patientData?.isPregnant || false}
              onChange={(e) => handleInputChange('isPregnant', e?.target?.checked)}
              description="Check if applicable for female patients"
            />
          </div>
        )}

        {/* Data Privacy Compliance */}
        <div className="col-span-full border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-success" />
              <span className="text-sm font-medium text-foreground">GDPR Compliance</span>
            </div>
            <button
              type="button"
              onClick={() => setShowSensitiveInfo(!showSensitiveInfo)}
              className="text-sm text-primary hover:text-primary/80 transition-colors duration-150"
            >
              {showSensitiveInfo ? 'Hide' : 'Show'} Sensitive Data
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            All patient data is anonymized and encrypted according to EU pharmaceutical regulations
          </p>
        </div>
      </div>
    </div>
  );
};

export default PatientInformationForm;